import type { NeighborhoodBusiness } from '../../../../mocks/neighborhoodDetails';

interface BusinessCardProps {
  business: NeighborhoodBusiness;
  accentColor: string;
}

export default function BusinessCard({ business, accentColor }: BusinessCardProps) {
  return (
    <article
      className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border group ${
        business.featured ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-100'
      }`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={business.image}
          alt={`${business.name} - ${business.type} in Roseville California`}
          title={`${business.name} Roseville CA`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

        <div className="absolute top-3 left-3 flex items-center gap-2">
          {business.featured && (
            <span className="px-2.5 py-1 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
              <i className="ri-star-fill text-xs"></i>
              Featured
            </span>
          )}
          {business.verified && (
            <span className="px-2.5 py-1 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
              <i className="ri-verified-badge-fill text-xs"></i>
              Verified
            </span>
          )}
        </div>

        {business.offer && (
          <div className="absolute bottom-3 left-3">
            <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-teal-700 text-xs font-bold rounded-lg whitespace-nowrap">
              {business.offer}
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${accentColor}12` }}
          >
            <i className={`${business.icon} text-lg`} style={{ color: accentColor }}></i>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors truncate">
                <a href={business.website} target="_blank" rel="noopener noreferrer nofollow" className="cursor-pointer">
                  {business.name}
                </a>
              </h4>
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">{business.priceRange}</span>
            </div>
            <p className="text-xs text-gray-500 italic">{business.type}</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{business.description}</p>

        {/* Highlights */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {business.highlights.slice(0, 3).map((h) => (
            <span key={h} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] rounded-md font-medium">
              {h}
            </span>
          ))}
        </div>

        {/* Info */}
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-line text-teal-500 text-sm"></i>
            </div>
            <span className="truncate text-xs">{business.address}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-teal-500 text-sm"></i>
            </div>
            <a href={`tel:${business.phone}`} className="hover:text-teal-600 cursor-pointer text-xs">{business.phone}</a>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-teal-500 text-sm"></i>
            </div>
            <span className="truncate text-[11px]">{business.hours}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`text-xs ${
                    i < Math.floor(business.rating)
                      ? 'ri-star-fill text-amber-400'
                      : i < business.rating
                      ? 'ri-star-half-fill text-amber-400'
                      : 'ri-star-line text-gray-300'
                  }`}
                ></i>
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-900">{business.rating}</span>
            <span className="text-xs text-gray-400">({business.reviews.toLocaleString()})</span>
          </div>

          <div className="flex gap-1.5">
            <a
              href={`tel:${business.phone}`}
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-teal-50 flex items-center justify-center transition-colors cursor-pointer"
              title="Call"
            >
              <i className="ri-phone-line text-gray-600 hover:text-teal-600 text-sm"></i>
            </a>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.name + ' ' + business.address)}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-teal-50 flex items-center justify-center transition-colors cursor-pointer"
              title="Get Directions"
            >
              <i className="ri-navigation-line text-gray-600 hover:text-teal-600 text-sm"></i>
            </a>
            <a
              href={business.website}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-8 h-8 rounded-lg bg-teal-50 hover:bg-teal-100 flex items-center justify-center transition-colors cursor-pointer"
              title="Visit Website"
            >
              <i className="ri-external-link-line text-teal-600 text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
